import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const isIgnorableDomMutationError = (error: Error | null | undefined): boolean => {
  if (!error) return false;
  const message = String(error.message || '').toLowerCase();
  return (
    message.includes("failed to execute 'removechild' on 'node'") ||
    message.includes('the node to be removed is not a child of this node')
  );
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): Partial<State> | null {
    if (isIgnorableDomMutationError(error)) {
      return null;
    }
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isIgnorableDomMutationError(error)) {
      console.warn('Ignored non-fatal DOM mutation error:', error.message);
      return;
    }
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-gray-400 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              className="bg-qr-primary hover:bg-qr-secondary px-6 py-2 rounded-lg text-white font-medium transition-colors"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;